// EAComponents.jsx
import React, { useState, useEffect } from 'react';
import { 
  AlertDialog,
  AlertDialogContent, 
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel
} from "../components/ui/alert-dialog";

// EAInstallButton Component
const EAInstallButton = ({ userId, isInstalled, isActive, onInstall, compact }) => {
  const [loading, setLoading] = useState(false);

  const handleInstall = async (e) => {
    // Prevent event propagation
    if (e) {
      e.stopPropagation();
    }
    
    try {
      setLoading(true);
      // Call the onInstall function with the userId
      await onInstall(userId);
    } catch (error) {
      console.error('Installation error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // If EA is both installed and active, show a success message instead of a button
  if (isInstalled && isActive) {
    return (
      <div className={`${compact ? 'px-2 py-1 text-xs' : 'px-4 py-2'} text-green-600 font-medium`}>
        {compact ? 'EA Active' : 'EA Successfully Activated'}
      </div>
    );
  }

  return (
    <button
      onClick={handleInstall}
      disabled={loading || isInstalled}
      className={`${compact ? 'px-2 py-1 text-xs rounded' : 'px-4 py-2 rounded-md'} ${
        isInstalled 
          ? 'bg-green-500 text-white cursor-not-allowed'
          : loading
          ? 'bg-gray-400 text-white cursor-not-allowed'
          : 'bg-blue-500 text-white hover:bg-blue-600'
      }`}
    >
      {isInstalled 
        ? compact ? 'Installed' : 'EA Installed' 
        : loading 
          ? compact ? 'Installing...' : 'Marking as Installed...' 
          : compact ? 'Install EA' : 'Mark EA Installed'
      }
    </button>
  );
};

// EAActivationButton Component with direct VPS activation
const EAActivationButton = ({ isInstalled, isActive, onActivate }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [mt5Account, setMt5Account] = useState('');
  const [mt5Password, setMt5Password] = useState('');
  const [mt5Server, setMt5Server] = useState('Swissquote-Instit-Server'); // Default server
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mt5Accounts, setMt5Accounts] = useState([]); 
  const [selectedAccountId, setSelectedAccountId] = useState(null); // Track selected MT5 account ID
  
  console.log("EAActivationButton - isInstalled:", isInstalled, "isActive:", isActive); // Debug log
  
  // Fetch user's MT5 accounts when dialog opens
  useEffect(() => {
    if (showDialog) {
      fetchMT5Accounts();
    }
  }, [showDialog]);
  
  const fetchMT5Accounts = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error("Authentication required");
        return;
      }
      
      const response = await fetch('/api/mt5-accounts', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch MT5 accounts');
      }
      
      const accounts = await response.json();
      setMt5Accounts(accounts);
      
      // Select the default account if available
      const defaultAccount = accounts.find(acc => acc.is_default);
      if (defaultAccount) {
        setSelectedAccountId(defaultAccount.id);
        setMt5Account(defaultAccount.account_number);
        if (defaultAccount.broker_server) {
          setMt5Server(defaultAccount.broker_server);
        }
      } else if (accounts.length > 0) {
        setSelectedAccountId(accounts[0].id);
        setMt5Account(accounts[0].account_number);
        if (accounts[0].broker_server) {
          setMt5Server(accounts[0].broker_server);
        }
      }
    } catch (error) {
      console.error('Error fetching MT5 accounts:', error);
      setError('Failed to load MT5 accounts. Please try again.');
    }
  };  const handleInitializeMT5 = async () => {
    if (!mt5Account.trim()) {
      setError('Please enter your MT5 account number');
      return;
    }
    
    if (!mt5Password.trim()) {
      setError('Please enter your MT5 password');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      console.log(`🚀 Initializing MT5 for account ${mt5Account} with server ${mt5Server}`);
      
      // Call the activation handler directly when Initialize MT5 is clicked
      // This will send the request to the backend which forwards to mt5_activation_service.py
      if (onActivate) {        try {
          console.log('📡 Sending MT5 activation request to VPS...');
          console.log('Selected Account ID:', selectedAccountId);
          const activationSuccess = await onActivate(mt5Account, mt5Password, mt5Server, selectedAccountId);
          console.log('VPS response received:', activationSuccess);
          
          if (activationSuccess) {
            console.log('✅ MT5 initialization and EA activation completed successfully');
            alert('EA activated successfully! The EA is now running on the VPS server.');
            
            // Reset form and close dialog
            resetDialog();
          } else {
            console.error('MT5 initialization failed - no success confirmation');
            throw new Error('MT5 initialization failed. Please check your credentials and try again.');
          }
        } catch (activationError) {
          console.error('MT5 initialization error:', activationError);
          throw activationError;
        }
      } else {
        console.error('No activation handler provided');
        throw new Error('System configuration error: No activation handler available.');
      }
      
    } catch (error) {
      console.error('MT5 initialization error:', error);
      setError(`MT5 initialization failed: ${error.message}. Please check your MT5 credentials and try again.`);
      setLoading(false);
    }
  };  const resetDialog = () => {
    setMt5Account('');
    setMt5Password('');
    setMt5Server('Swissquote-Instit-Server');
    setError('');
    setShowDialog(false);
  };
  // Don't render button if EA is both installed and active
  if (isInstalled && isActive) {
    return (
      <div className="px-4 py-2 text-green-600 font-medium">
        EA Successfully Activated
      </div>
    );
  }
    return (
    <>
      <button
        onClick={() => setShowDialog(true)}
        disabled={!isInstalled}
        className={`px-4 py-2 rounded-md ${
          !isInstalled 
            ? 'bg-gray-300 cursor-not-allowed'
            : 'bg-blue-500 text-white hover:bg-blue-600'
        }`}
      >
        {isInstalled ? 'Activate EA' : 'EA Not Installed'}
      </button>      <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Initialize MT5 Account
            </AlertDialogTitle>
            <AlertDialogDescription>
              Please enter your MT5 account details to set up your terminal on the VPS. Your password is only used to connect to MT5 and is not stored in our database. After clicking "Initialize MT5", the system will connect to the VPS server and activate the EA.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4 my-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select MT5 Account
              </label>
              {mt5Accounts.length > 0 ? (
                <select
                  value={selectedAccountId || ''}
                  onChange={(e) => {
                    const accountId = Number(e.target.value);
                    setSelectedAccountId(accountId);
                    const account = mt5Accounts.find(acc => acc.id === accountId);
                    if (account) {
                      setMt5Account(account.account_number);
                      if (account.broker_server) {
                        setMt5Server(account.broker_server);
                      }
                    }
                  }}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {mt5Accounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.account_number} - {account.broker_name} {account.is_default ? '(Default)' : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="text-sm text-red-500">No MT5 accounts found. Please add an MT5 account first.</div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                MT5 Account Number
              </label>
              <input
                type="text"
                value={mt5Account}
                onChange={(e) => setMt5Account(e.target.value)}
                placeholder="Enter MT5 Account Number"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={mt5Accounts.length > 0}
                readOnly={mt5Accounts.length > 0}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                MT5 Password
              </label>
              <input
                type="password"
                value={mt5Password}
                onChange={(e) => setMt5Password(e.target.value)}
                placeholder="Enter MT5 Password"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                MT5 Server
              </label>
              <select
                value={mt5Server}
                onChange={(e) => setMt5Server(e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="Swissquote-Server">Swissquote-Server (Default)</option>
                <option value="Swissquote-Demo-Server">Swissquote-Demo-Server</option>
                <option value="SwissquoteLtd-Server">SwissquoteLtd-Server</option>
                <option value="Swissquote-Live-Server">Swissquote-Live-Server</option>
                <option value="Swissquote-Instit-Server">Swissquote-Instit-Server</option>
              </select>
            </div>
          </div>
          
          {error && (
            <p className="mt-2 text-sm text-red-600">{error}</p>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel 
              disabled={loading}
              onClick={resetDialog}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleInitializeMT5}
              disabled={(!mt5Account.trim() || !mt5Password.trim()) || loading}
              className={`${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {loading 
                ? 'Initializing MT5 & Activating EA...' 
                : 'Initialize MT5'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// EAStatusDisplay Component
const EAStatusDisplay = ({ isInstalled, isActive, compact, accountNumber }) => {
  // Add enhanced debug logging 
  console.log('EAStatusDisplay rendering with:', { 
    isInstalled, 
    isActive, 
    accountNumber,
    timestamp: new Date().toISOString() 
  });
  
  // Force explicit boolean values to prevent unexpected rendering behaviors
  const installed = !!isInstalled;
  const active = installed && !!isActive;
  
  if (compact) {
    return (
      <div className="inline-flex items-center space-x-2">
        <span className={`w-2 h-2 rounded-full ${
          isInstalled ? (isActive ? 'bg-green-500' : 'bg-yellow-500') : 'bg-red-500'
        }`}></span>
        <span className="text-xs">
          {isInstalled 
            ? (isActive ? 'Active' : 'Installed') 
            : 'Not Installed'}
        </span>
      </div>
    );
  }
    return (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="text-lg font-semibold mb-2">EA Status</h3>
      {accountNumber && (
        <div className="text-sm text-gray-600 mb-3">
          Account: {accountNumber}
        </div>
      )}
      <div className="space-y-2">
        <div className="flex items-center">
          <span className={`w-3 h-3 rounded-full mr-2 ${
            installed ? 'bg-green-500' : 'bg-red-500'
          }`}></span>
          <span>Installation: {installed ? 'Installed' : 'Not Installed'}</span>
        </div>
        <div className="flex items-center">
          <span className={`w-3 h-3 rounded-full mr-2 ${
            active ? 'bg-green-500' : 'bg-gray-500'
          }`}></span>
          <span>Status: {active ? 'Active' : 'Inactive'}</span>
        </div>
      </div>
    </div>
  );
};

// Make sure all components are properly exported
export { EAInstallButton, EAActivationButton, EAStatusDisplay };